import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldAlert, ArrowRight } from 'lucide-react';

const inputCls = 'rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0 text-[16px]';
const labelCls = 'uppercase tracking-luxe text-[11px] font-semibold text-ink-muted mb-2 block';
const ctaCls = 'w-full inline-flex items-center justify-center gap-3 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium py-4 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed';

export default function ForceResetPasswordPage() {
  const { user, forceResetPassword, logout } = useAuth();
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) navigate('/login');
    else if (!user.must_reset_password) navigate('/');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await forceResetPassword(oldPassword, newPassword);
      const stored = JSON.parse(localStorage.getItem('besq_user') || '{}');
      localStorage.setItem('besq_user', JSON.stringify({ ...stored, must_reset_password: false }));
      navigate(stored.role && stored.role !== 'CUSTOMER' ? '/admin' : '/');
    } catch (err) {
      setError(err?.response?.data?.error || 'Reset failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] flex items-center justify-center px-6 py-16 bg-background" data-testid="force-reset-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-gold text-gold mb-6">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="eyebrow mb-4">Security</div>
          <h1 className="font-display font-light text-ink leading-none tracking-[-0.015em]" style={{ fontSize: 44 }}>Set a new password</h1>
          <p className="mt-4 text-ink/70" style={{ fontSize: 15 }}>For security, please choose a new password before continuing.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="force-reset-form">
          <div>
            <Label htmlFor="op" className={labelCls}>Current Password</Label>
            <Input id="op" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} required data-testid="old-password-input" className={inputCls} />
          </div>
          <div>
            <Label htmlFor="np" className={labelCls}>New Password</Label>
            <Input id="np" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} placeholder="At least 6 characters" data-testid="new-password-input" className={inputCls} />
          </div>
          <div>
            <Label htmlFor="cp" className={labelCls}>Confirm New Password</Label>
            <Input id="cp" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} data-testid="confirm-new-password-input" className={inputCls} />
          </div>

          {error && <p className="text-[13px] text-destructive uppercase tracking-luxe font-medium" data-testid="force-reset-error">{error}</p>}

          <button type="submit" disabled={loading} className={ctaCls} style={{ fontSize: 13 }} data-testid="force-reset-btn">
            {loading ? 'Updating…' : (<>Update Password & Continue <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <button type="button" onClick={() => { logout(); navigate('/login'); }} className="w-full text-center uppercase tracking-luxe text-[12px] font-medium text-ink-muted hover:text-ink pt-2" data-testid="force-reset-logout">
            Log out instead
          </button>
        </form>
      </div>
    </div>
  );
}
